import {
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useMemo, useState, useEffect } from 'react';
import { Loader, Table } from '../../components';
import { IDropdownResponse } from '../../models';

interface ICheckboxListTable {
  data: IDropdownResponse[];
  isLoading?: boolean;
  selectedItems: IDropdownResponse[];
  setSelectedItems: React.Dispatch<React.SetStateAction<IDropdownResponse[]>>;
}

export const CheckboxListTable: FC<ICheckboxListTable> = ({
  data,
  isLoading,
  selectedItems,
  setSelectedItems
}) => {
  const classes = useStyles();
  const [isAllSelected, setSelectedAll] = useState(false);

  useEffect(() => {
    setSelectedAll(false);
  }, [data]);

  const handleDelete = chipToDelete => () => {
    setSelectedItems(selectedItems =>
      selectedItems.filter(selectedItem => selectedItem.value !== chipToDelete.value)
    );
  };

  const columns = useMemo(() => {
    return [
      {
        id: 'checkbox',
        accessor: '',
        Header: ' ',
        overrideWidth: 50,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDropdownResponse } };
        }) => {
          const tableItem = original;
          const ids = selectedItems.map(tableItem => tableItem);
          const isSelected = ids.includes(tableItem);

          return (
            <FormControlLabel
              classes={{
                label: classes.tableCheckbox
              }}
              color="primary"
              control={
                <Checkbox
                  id="items-checkbox"
                  checked={isSelected}
                  onChange={() => {
                    isSelected
                      ? setSelectedItems(
                          selectedItems.filter(
                            selectedItem => selectedItem.value !== tableItem.value
                          )
                        )
                      : setSelectedItems([...selectedItems, tableItem]);
                  }}
                />
              }
              label=""
            />
          );
        }
      },
      {
        Header: ' ',
        accessor: 'description',
        sort: false
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedItems]);

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && data && (
        <Loader type="overlay" position="centered">
          {'Loading...'}
        </Loader>
      )}
      {selectedItems.length !== 0 && (
        <>
          <Grid container>
            <Grid item xs={12} sm={9}>
              <List className={classes.selectedItemsList}>
                <ListItem key={'items-selected'}>
                  <Typography>Items selected:</Typography>
                </ListItem>
                {selectedItems.map(selectedItem => {
                  return (
                    <ListItem key={selectedItem.value}>
                      <Chip label={selectedItem.shorthand} onDelete={handleDelete(selectedItem)} />
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      <div className={classes.actionsWrapper}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isAllSelected}
              id="items-checkbox"
              onChange={() => {
                if (isAllSelected) {
                  setSelectedItems([]);
                } else {
                  setSelectedItems(data);
                }
                setSelectedAll(!isAllSelected);
              }}
            />
          }
          label="Select All"
        />
      </div>
      <Divider />
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileBreakPoint={1400}
        mobileProps={() => {}}
        containerClasses={classes.tableContainer}
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionsWrapper: {
    padding: theme.spacing(0.75, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  tableContainer: {
    '& .MuiTableHead-root': {
      display: 'none'
    }
  },
  tableCheckbox: {
    marginRight: 0
  },
  selectedItemsList: {
    display: 'flex',
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: theme.spacing(0.25, 0),
    margin: 0,
    '& .MuiListItem-root': {
      width: 'auto',
      padding: theme.spacing(0.25, 0.25)
    }
  }
}));
